import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { Formik, Form, useFormikContext, FormikHelpers, FastField, Field } from "formik";
import * as Yup from "yup";
import HorizontalSeparator from "../common/HorizontalSeparator";
import { Box, Tabs, Tab } from "@mui/material";
import { useGlobalState } from "../../context/useGlobalState";
import { updateSingleTrack } from "../../api/singleTrack";
import { getProjects, getProject } from "../../api";
import Button from "../ui/Button";
import { FileInput } from "../common/FileInput";
import { uploadFileToS3Complete } from "../../api/s3";
import ImageList from "../images/ImageList";
import { TextField, NumberField } from "../common/FormFields";
import FormGroup from "../common/FormGroup";
import ImageSpecsBadges from "../common/ImageSpecsBadges";
import { useImageValidation } from "../../hooks/useImageValidation";
import { Tooltip } from "@mui/material";
import AudioPlayerRewind from "../common/AudioPlayer";
import Dropdown from "../common/Dropdown";
import RadioButton from "../common/RadioButton";
import { SingleTrackResponse, CompanySingleTrackFolder } from "../../types/requestsOutputs";
import { useNotification } from "../../context/NotificationContext";
import logo from "../../assets/images/logo-rewind.png";
import activeRadio from "../../assets/images/radio_active.png";
import inactiveRadio from "../../assets/images/radio_inactive.png";
import { IMAGE_FORMAT, IMAGE_MAX_SIZE_200_KB } from "../../utils/image";
import studio from "./StudioModal.module.css";
import stepStyles from "../../pages/track/create/createStep.module.css";
import infoStyles from "./ModalInfoFields.module.css";
import audioForm from "../common/AudioPlayerForm.module.css";

const audioAccept = {
    "audio/mpeg": [".mp3"],
    "audio/wav": [".wav"],
};

interface LocalizedContent {
    name: string;
    description?: string;
    audioUrl: string;
    useProject: boolean;
    selectedProject: string;
    selectedAudioOption: string;
}

interface UnifiedFormValues {
    localizedContent: {
        [key: string]: LocalizedContent;
    };
    // Information block fields (all optional)
    address?: string;
    coordinates?: string;
    phone?: string;
    linkUrl?: string;
    downloadLink?: string;
    // Common fields
    pictures: string[];
    type: "story" | "event";
    position?: number;
}

interface GroupedSingleTrack {
    mainTrack: SingleTrackResponse["singleTrack"];
    localizedVersions: SingleTrackResponse[];
}

interface UnifiedEditSingleTrackModalProps {
    singleTrack: GroupedSingleTrack;
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    hub?: CompanySingleTrackFolder | null;
}

/** Hub languages plus any locales still on the track but dropped from the hub list (so content stays editable). */
function buildMergedHubLanguages(
    hub: CompanySingleTrackFolder | null | undefined,
    singleTrack: GroupedSingleTrack,
    languagesAI: any[] | undefined,
): { code: string; name: string }[] {
    const list: { code: string; name: string }[] = [];
    const seen = new Set<string>();
    const labelForCode = (code: string) => {
        const lang = languagesAI?.find((l: any) => String(l.code).toUpperCase() === code.toUpperCase());
        return lang ? lang.label : code;
    };

    if (hub?.availableLanguagesTab && hub.availableLanguagesTab.length > 0) {
        for (const langCode of hub.availableLanguagesTab) {
            const c = String(langCode).toUpperCase();
            if (seen.has(c)) continue;
            seen.add(c);
            list.push({ code: c, name: labelForCode(c) });
        }
    }

    const fromTrack = singleTrack.localizedVersions?.map((v) => String(v.language).toUpperCase()).filter(Boolean) || [];
    for (const c of fromTrack) {
        if (seen.has(c)) continue;
        seen.add(c);
        list.push({ code: c, name: labelForCode(c) });
    }

    if (list.length > 0) return list;
    return [
        { code: "EN", name: "English" },
        { code: "FR", name: "Français" },
    ];
}

const UnifiedEditSingleTrackForm = ({ languageTabs }: { languageTabs: { code: string; name: string }[] }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const LANGUAGES = languageTabs;

    const [activeLanguageTab, setActiveLanguageTab] = useState(0);
    const [projects, setProjects] = useState<any[]>([]);
    const [projectsData, setProjectsData] = useState<{ [key: string]: any }>({});
    const [loadingProjects, setLoadingProjects] = useState(true);

    const { values, setFieldValue, errors, touched } = useFormikContext<UnifiedFormValues>();
    const imageValidation = useImageValidation({
        type: "hubStory",
        imageUrl: values.pictures[0] || undefined,
    });

    // Check if all required data is loaded
    const isDataLoading = loadingProjects;

    // Handle language tab change
    const handleLanguageTabChange = (newTabIndex: number) => {
        setActiveLanguageTab(newTabIndex);
    };

    // Fetch projects for audio selection
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const token = localStorage.getItem("token");
                if (!token) {
                    setLoadingProjects(false);
                    return;
                }

                const response = await getProjects({ token });
                if (response) {
                    setProjects(response);
                }
            } catch (error) {
                console.error("Error fetching projects:", error);
            } finally {
                setLoadingProjects(false);
            }
        };

        fetchProjects();
    }, []);

    // Fetch project details when needed
    const fetchProjectDetails = async (projectId: string) => {
        if (projectsData[projectId]) return projectsData[projectId];

        try {
            const token = localStorage.getItem("token");
            if (!token) return null;

            const response = await getProject({ token, projectId });
            if (response) {
                setProjectsData((prev) => ({
                    ...prev,
                    [projectId]: response,
                }));
                return response;
            }
        } catch (error) {
            console.error("Error fetching project details:", error);
        }
        return null;
    };

    const handlePicturesSelected = useCallback(
        async (files: File | File[]) => {
            const fileList = Array.isArray(files) ? files : [files];
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("No authentication token found");
                return;
            }

            await Promise.all(fileList.map((file) => imageValidation.validateImage(file)));

            Promise.all(
                fileList.map((file) =>
                    uploadFileToS3Complete(
                        {
                            file,
                            isPublic: true, // Public files for general uploads
                            isFolder: false, // No folder structure for general uploads
                        },
                        token,
                    ),
                ),
            )
                .then((results) => {
                    setFieldValue("pictures", [...values.pictures, ...results]);
                })
                .catch(console.error);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setFieldValue, values.pictures, imageValidation.validateImage],
    );

    const setPicturesFromList = useCallback(
        (pictures: string[]) => {
            setFieldValue("pictures", pictures);
        },
        [setFieldValue],
    );

    // Handle audio file upload for specific language
    const handleAudioFileSelected = useCallback(
        (langCode: string) => {
            return (files: File | File[]) => {
                const file = Array.isArray(files) ? files[0] : files;
                if (file) {
                    const token = localStorage.getItem("token");
                    if (!token) {
                        console.error("No authentication token found");
                        return;
                    }

                    uploadFileToS3Complete(
                        {
                            file,
                            isPublic: true, // Public files for general uploads
                            isFolder: false, // No folder structure for general uploads
                        },
                        token,
                    )
                        .then((result) => {
                            setFieldValue(`localizedContent.${langCode}.audioUrl`, result);
                        })
                        .catch(console.error);
                }
            };
        },
        [setFieldValue],
    );

    // Handle project selection for audio
    const handleProjectSelection = useCallback(
        (langCode: string, projectId: string) => {
            setFieldValue(`localizedContent.${langCode}.selectedProject`, projectId);
            setFieldValue(`localizedContent.${langCode}.selectedAudioOption`, "");
            setFieldValue(`localizedContent.${langCode}.audioUrl`, "");
            if (projectId) {
                fetchProjectDetails(projectId);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setFieldValue],
    );

    // Handle audio selection from project
    const handleAudioSelection = useCallback(
        (langCode: string, audioUrl: string) => {
            setFieldValue(`localizedContent.${langCode}.selectedAudioOption`, audioUrl);
            setFieldValue(`localizedContent.${langCode}.audioUrl`, audioUrl);
        },
        [setFieldValue],
    );

    // Toggle project mode for specific language
    const handleProjectToggle = useCallback(
        (langCode: string, useProject: boolean) => {
            setFieldValue(`localizedContent.${langCode}.useProject`, useProject);
            if (!useProject) {
                setFieldValue(`localizedContent.${langCode}.selectedProject`, "");
                setFieldValue(`localizedContent.${langCode}.selectedAudioOption`, "");
            }
        },
        [setFieldValue],
    );

    // Remove audio for specific language
    const handleRemoveAudio = useCallback(
        (langCode: string) => {
            setFieldValue(`localizedContent.${langCode}.audioUrl`, "");
            setFieldValue(`localizedContent.${langCode}.selectedProject`, "");
            setFieldValue(`localizedContent.${langCode}.selectedAudioOption`, "");
            setFieldValue(`localizedContent.${langCode}.useProject`, false);
        },
        [setFieldValue],
    );

    // Get audio options for specific language and project
    const getAudioOptionsForLanguage = (langCode: string, projectId: string) => {
        const project = projectsData[projectId];
        if (!project) return [];

        return (
            project.audioAI
                ?.filter((audio: any) => audio.language === langCode)
                ?.sort((a: any, b: any) => a.position - b.position)
                .map((audio: any) => ({
                    value: audio.audioUrl,
                    label: audio.name,
                })) || []
        );
    };

    // Loading spinner component
    if (isDataLoading) {
        return (
            <div className="form-loading-container">
                <div className="form-loading-spinner"></div>
                <p className="form-loading-text">{t("loadingFormData")}</p>
            </div>
        );
    }

    return (
        <Form>
            <div>
                {/* Position */}
                <FormGroup
                    title={t("position")}
                    subtitle={t("positionDescription")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <FastField name="position" component={NumberField} placeholder={t("enterPosition")} min={0} />
                </FormGroup>

                {/* Picture - single image */}
                <FormGroup title={t("pictures")} className={`${stepStyles.stepDescription} form-group-spacing`}>
                    <ImageSpecsBadges spec="hubStory" warnings={imageValidation.warnings} />
                    <div className={`${stepStyles.fileUpload} form-file-upload-constrained`}>
                        <FileInput
                            onFileSelected={handlePicturesSelected}
                            showButton={true}
                            buttonVariant="outline"
                            multiple={false}
                            acceptedFileTypes={IMAGE_FORMAT}
                            maxSize={IMAGE_MAX_SIZE_200_KB}
                            maxSizeLabel={t("imageMaxSize200kb")}
                        />
                    </div>

                    {values.pictures.length > 0 && (
                        <div className={`photo-gallery form-photo-gallery-constrained ${stepStyles.previewGap}`}>
                            <ImageList pictures={values.pictures} setPictures={setPicturesFromList} />
                        </div>
                    )}
                </FormGroup>

                {/* Information Block - all optional */}
                <FormGroup
                    title={t("contactInformation")}
                    subtitle={t("downloadLinkDescription")}
                    className={`${stepStyles.stepDescription} form-group-spacing`}
                >
                    <div className={infoStyles.fields}>
                        {/* Address */}
                        <div>
                            <label className={infoStyles.label}>{t("address")}</label>
                            <FastField name="address" placeholder={t("enterAddress")} component={TextField} />
                        </div>

                        {/* Coordinates – only shown when address is filled */}
                        {values.address && values.address.trim() !== "" && (
                            <div>
                                <label className={infoStyles.label}>{t("coordinates")}</label>
                                <Field name="coordinates" placeholder={t("enterCoordinates")} component={TextField} />
                                {touched.coordinates && errors.coordinates && (
                                    <span className="error-message">{errors.coordinates}</span>
                                )}
                            </div>
                        )}

                        {/* Phone */}
                        <div>
                            <label className={infoStyles.label}>{t("phone")}</label>
                            <FastField name="phone" placeholder={t("phoneNumber")} component={TextField} />
                        </div>

                        {/* Website / Download link – mutually exclusive */}
                        <div className={infoStyles.exclusiveBlock}>
                            <div
                                className={
                                    values.downloadLink && values.downloadLink.trim() !== ""
                                        ? infoStyles.fieldGroupDisabled
                                        : undefined
                                }
                            >
                                <label className={infoStyles.label}>{t("websiteLinkLabel")}</label>
                                <Field
                                    name="linkUrl"
                                    placeholder={t("websiteLinkPlaceholder")}
                                    component={TextField}
                                    disabled={!!(values.downloadLink && values.downloadLink.trim() !== "")}
                                />
                            </div>

                            <div
                                className={
                                    values.linkUrl && values.linkUrl.trim() !== ""
                                        ? infoStyles.fieldGroupDisabled
                                        : undefined
                                }
                            >
                                <label className={infoStyles.label}>{t("downloadLinkLabel")}</label>
                                <Field
                                    name="downloadLink"
                                    placeholder={t("downloadLinkPlaceholder")}
                                    component={TextField}
                                    disabled={!!(values.linkUrl && values.linkUrl.trim() !== "")}
                                />
                                {touched.downloadLink && errors.downloadLink && (
                                    <span className="error-message">{errors.downloadLink}</span>
                                )}
                            </div>
                            <p className={infoStyles.exclusiveHint}>{t("mutuallyExclusiveHint")}</p>
                        </div>
                    </div>
                </FormGroup>

                {/* Localized Content */}
                <h3 className="settings-title">{t("content")}</h3>
                <HorizontalSeparator />

                <Box
                    sx={{
                        borderBottom: 1,
                        borderColor: "divider",
                        mt: 2,
                        mb: 2,
                    }}
                >
                    <Tabs
                        value={activeLanguageTab}
                        onChange={(_, newValue) => handleLanguageTabChange(newValue)}
                        sx={{
                            position: "sticky",
                            top: 0,
                            backgroundColor: "white",
                            zIndex: 10,
                            borderBottom: "1px solid #e0e0e0",
                            "& .MuiTabs-flexContainer": {
                                minHeight: "48px",
                            },
                            "& .MuiTab-root": {
                                minHeight: "48px",
                                textTransform: "none",
                                fontWeight: 500,
                            },
                        }}
                    >
                        {LANGUAGES.map((lang, index) => (
                            <Tab key={lang.code} label={lang.name} />
                        ))}
                    </Tabs>
                </Box>

                {/* Tab Content Container with stable dimensions */}
                <Box
                    sx={{
                        position: "relative",
                        minHeight: "600px", // Stable container height
                        overflow: "hidden",
                    }}
                >
                    {LANGUAGES.map((lang, index) => {
                        const langCode = lang.code;
                        const currentContent = values.localizedContent[langCode] || {
                            name: "",
                            description: "",
                            audioUrl: "",
                            useProject: false,
                            selectedProject: "",
                            selectedAudioOption: "",
                        };
                        const projectOptions = projects.map((project) => ({
                            value: project.id,
                            label: project.name,
                        }));
                        const audioOptions = currentContent.selectedProject
                            ? getAudioOptionsForLanguage(langCode, currentContent.selectedProject)
                            : [];

                        return (
                            <Box
                                key={lang.code}
                                sx={{ display: activeLanguageTab === index ? "block" : "none", pt: 0, pb: 2 }}
                            >
                                <FormGroup
                                    title={`${t("name")} (${lang.name})`}
                                    className={`${stepStyles.stepDescription} align-start`}
                                    style={{ marginTop: 0, marginBottom: 20 }}
                                >
                                    <FastField
                                        name={`localizedContent.${langCode}.name`}
                                        placeholder={t("enterName")}
                                        component={TextField}
                                    />
                                </FormGroup>

                                {/* Audio Section */}
                                <FormGroup
                                    title={`${t("audio")} (${lang.name})`}
                                    className={stepStyles.stepDescription}
                                    style={{ marginTop: 0, marginBottom: 20 }}
                                >
                                    <div className="editor-section">
                                        <FileInput
                                            onFileSelected={handleAudioFileSelected(langCode)}
                                            placeholder={t("dragAndDropYourAudioFileHereOrClickToSelectOne")}
                                            acceptedFileTypes={audioAccept}
                                        />

                                        <div className="radio-text-bold" style={{ marginTop: "20px" }}>
                                            <RadioButton
                                                label={t("addFromYourProjects")}
                                                checked={currentContent.useProject}
                                                onChange={() =>
                                                    handleProjectToggle(langCode, !currentContent.useProject)
                                                }
                                                activeImage={activeRadio}
                                                inactiveImage={inactiveRadio}
                                                center={true}
                                            />
                                        </div>

                                        {currentContent.useProject && (
                                            <div className="dropdown-row" style={{ marginTop: "10px", marginLeft: 0 }}>
                                                <Dropdown
                                                    label=""
                                                    options={projectOptions}
                                                    onChange={(value) => handleProjectSelection(langCode, value)}
                                                    placeholder={t("selectProject")}
                                                    loading={false}
                                                    error={false}
                                                    value={currentContent.selectedProject}
                                                />
                                                {currentContent.selectedProject && (
                                                    <Dropdown
                                                        label=""
                                                        options={audioOptions}
                                                        onChange={(value) => handleAudioSelection(langCode, value)}
                                                        loading={false}
                                                        error={false}
                                                        placeholder={t("selectAnAudio")}
                                                        value={currentContent.selectedAudioOption}
                                                    />
                                                )}
                                            </div>
                                        )}

                                        {currentContent.audioUrl && (
                                            <div className={audioForm.container}>
                                                <div className={audioForm.header}>
                                                    <span className={audioForm.title}>
                                                        {t("audioTrack")} ({lang.name})
                                                    </span>
                                                    <Button
                                                        label={t("remove")}
                                                        onClick={() => handleRemoveAudio(langCode)}
                                                        variant="danger"
                                                        isSmall
                                                        preventDefault
                                                        className={audioForm.removeBtn}
                                                    />
                                                </div>
                                                <HorizontalSeparator />
                                                <div className={audioForm.content}>
                                                    <AudioPlayerRewind
                                                        title=""
                                                        src={currentContent.audioUrl}
                                                        isAutoPlay={false}
                                                        hideDownload={true}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </FormGroup>

                                <FormGroup
                                    title={`${t("description")} (${lang.name})`}
                                    className={`${stepStyles.stepDescription} form-group-spacing`}
                                    style={{ marginTop: 0, marginBottom: 20 }}
                                >
                                    <FastField
                                        name={`localizedContent.${langCode}.description`}
                                        placeholder={t("enterDescription")}
                                        component={TextField}
                                        size="big-with-border"
                                        numberOfLines={4}
                                        fullWidth
                                    />
                                </FormGroup>
                            </Box>
                        );
                    })}
                </Box>
            </div>
        </Form>
    );
};

const UnifiedEditSingleTrackModal: React.FC<UnifiedEditSingleTrackModalProps> = ({
    singleTrack,
    isOpen,
    onClose,
    onSuccess,
    hub,
}) => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();
    const modalContentRef = useRef<HTMLDivElement>(null);
    const { data } = useGlobalState();

    const LANGUAGES = buildMergedHubLanguages(hub, singleTrack, data.languagesAI);

    // Helper function to populate initial values from existing singleTrack data
    const getInitialValues = (): UnifiedFormValues => {
        const track = singleTrack.mainTrack;

        // Find localizations by language
        const getLocalizationByLang = (lang: string) => {
            return singleTrack.localizedVersions?.find((v) => v.language.toUpperCase() === lang.toUpperCase());
        };

        // Get point localized address by language
        const getAddressByLang = (lang: string) => {
            return (
                track.point?.pointLocalized?.find((p: any) => p.language.toUpperCase() === lang.toUpperCase())
                    ?.formattedAddress || ""
            );
        };

        // Build localized content based on hub languages or existing languages
        const localizedContent: { [key: string]: LocalizedContent } = {};
        LANGUAGES.forEach((lang) => {
            const localization = getLocalizationByLang(lang.code);
            localizedContent[lang.code] = {
                name: localization?.name || "",
                description: localization?.description || "",
                audioUrl: localization?.audiotrack?.file || "",
                useProject: false,
                selectedProject: "",
                selectedAudioOption: "",
            };
        });

        return {
            localizedContent,
            // Information block fields
            address: getAddressByLang(LANGUAGES[0]?.code || "EN") || "",
            coordinates: track.point?.lat && track.point?.lng ? `${track.point.lat}, ${track.point.lng}` : "",
            phone: track.phone || "",
            linkUrl: track.linkUrl || "",
            downloadLink: (track as any).downloadLink || "",
            pictures: track.pictures?.map((p) => p.url) || [],
            type: "story",
            position: track.position || 0,
        };
    };

    // Validation schema
    const validationSchema = Yup.object()
        .shape({
            type: Yup.string().oneOf(["story", "event"]).required(),
            pictures: Yup.array().of(Yup.string()),
            position: Yup.number(),

            // Information block fields (all optional)
            address: Yup.string().nullable(),
            coordinates: Yup.string()
                .nullable()
                .test("is-valid-coordinates", t("coordinatesMustBeValid"), function (value) {
                    if (!value || value.trim() === "") return true;
                    const parts = value.split(",").map((val: string) => val.trim());
                    if (parts.length !== 2) return false;
                    const [lat, lng] = parts.map((val: string) => parseFloat(val));
                    return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
                })
                .test("coordinates-required-with-address", t("coordinatesRequiredWithAddress"), function (value) {
                    const { address } = this.parent;
                    if (address && address.trim() !== "" && (!value || value.trim() === "")) {
                        return false;
                    }
                    return true;
                }),
            phone: Yup.string().nullable(),
            linkUrl: Yup.string().nullable(),
            downloadLink: Yup.string()
                .nullable()
                .test("mutual-exclusion-download", t("downloadLinkMutualExclusion"), function (value) {
                    const { linkUrl } = this.parent;
                    if (value && value.trim() !== "" && linkUrl && linkUrl.trim() !== "") {
                        return false;
                    }
                    return true;
                }),

            // Localized content validation
            localizedContent: Yup.lazy(() => {
                const shape: any = {};
                LANGUAGES.forEach((lang) => {
                    shape[lang.code] = Yup.object({
                        name: Yup.string(),
                        description: Yup.string(),
                        audioUrl: Yup.string(),
                        useProject: Yup.boolean(),
                        selectedProject: Yup.string(),
                        selectedAudioOption: Yup.string(),
                    });
                });
                return Yup.object(shape);
            }),
        })
        .test("at-least-one-name", t("atLeastOneNameRequired"), function (values) {
            const hasAtLeastOneName = LANGUAGES.some((lang) => {
                const content = values.localizedContent?.[lang.code];
                return content?.name && content.name.trim() !== "";
            });

            if (!hasAtLeastOneName) {
                return this.createError({
                    path: `localizedContent.${LANGUAGES[0]?.code}.name`,
                    message: t("atLeastOneNameRequired"),
                });
            }

            return true;
        });

    // Handle form submission
    const handleSubmit = async (values: UnifiedFormValues, formikHelpers: FormikHelpers<UnifiedFormValues>) => {
        const { setSubmitting } = formikHelpers;
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("Missing token");
            return;
        }

        try {
            const trimmedAddress = values.address?.trim() ?? "";
            const payload: any = {
                type: "story" as const,
                localizedData: Object.entries(values.localizedContent).map(([langCode, content]) => ({
                    name: content.name,
                    description: content.description || null,
                    language: langCode,
                    audioUrl: content.audioUrl || null,
                    address: trimmedAddress !== "" ? trimmedAddress : null,
                })),
                coverImage: values.pictures?.[0] || null,
                pictures: values.pictures.map((url, index) => ({
                    url,
                    position: index,
                })),
                position: values.position,
                // Information block fields
                phone: values.phone || null,
                linkUrl: values.linkUrl || null,
                downloadLink: values.downloadLink || null,
            };

            // Coordinates follow the address: no address → clear the map point too.
            const coordsTrimmed = trimmedAddress !== "" ? (values.coordinates?.trim() ?? "") : "";
            const hadMapPoint =
                singleTrack.mainTrack.point != null &&
                typeof singleTrack.mainTrack.point.lat === "number" &&
                typeof singleTrack.mainTrack.point.lng === "number";
            if (coordsTrimmed !== "") {
                payload.point = {
                    latitude: parseFloat(coordsTrimmed.split(",")[0].trim()),
                    longitude: parseFloat(coordsTrimmed.split(",")[1].trim()),
                };
            } else if (hadMapPoint) {
                payload.point = { latitude: null, longitude: null };
            }

            await updateSingleTrack({
                token,
                trackId: singleTrack.mainTrack.id,
                data: payload,
            });

            showNotification({ message: t("singleTrackUpdatedSuccessfully"), type: "success" });
            onClose();
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error("Error updating single track:", err);
            showNotification({ message: t("errorUpdatingSingleTrack"), type: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    const flattenErrors = (errors: any): string[] => {
        const result: string[] = [];
        Object.values(errors).forEach((value) => {
            if (typeof value === "string") {
                result.push(value);
            } else if (typeof value === "object" && value !== null) {
                result.push(...flattenErrors(value));
            }
        });
        return result;
    };

    if (!isOpen) return null;

    const modal = (
        <div className={studio.overlay}>
            <div
                className={studio.singleTrackContent}
                ref={modalContentRef}
                style={{
                    maxWidth: "90vw",
                    width: "800px",
                    maxHeight: "90vh",
                    padding: "0",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <div
                    style={{
                        padding: "20px 20px 10px 20px",
                        borderBottom: "1px solid #eee",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexShrink: 0,
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                        <img src={logo} alt="Logo" className={studio.brandLogo} />
                        <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "bold" }}>{t("editSingleTrack")}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: "none",
                            border: "none",
                            fontSize: "24px",
                            cursor: "pointer",
                            color: "#666",
                            padding: "5px",
                            borderRadius: "4px",
                        }}
                    >
                        ×
                    </button>
                </div>

                <div
                    style={{
                        flex: 1,
                        overflow: "auto",
                        padding: "20px 20px 0 20px",
                    }}
                >
                    <Formik
                        initialValues={getInitialValues()}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                        key={`${singleTrack.mainTrack.id}-${i18n.language}`}
                        validateOnMount
                        enableReinitialize={true}
                        validateOnChange={true}
                        validateOnBlur={true}
                    >
                        {({ isSubmitting, submitForm, errors }) => (
                            <>
                                <UnifiedEditSingleTrackForm languageTabs={LANGUAGES} />

                                <div
                                    style={{
                                        marginTop: "30px",
                                        padding: "20px",
                                        borderTop: "1px solid #eee",
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        gap: "10px",
                                        position: "sticky",
                                        bottom: 0,
                                        backgroundColor: "white",
                                        zIndex: 10,
                                    }}
                                >
                                    <Button
                                        variant="cancel"
                                        label={t("cancel")}
                                        onClick={onClose}
                                        disabled={isSubmitting}
                                    />
                                    <Tooltip
                                        title={
                                            Object.keys(errors).length > 0
                                                ? t("formIncompleteTooltip", {
                                                      fields: flattenErrors(errors).join(", "),
                                                  })
                                                : ""
                                        }
                                        arrow
                                    >
                                        <span>
                                            <Button
                                                variant="primary"
                                                label={t("updateSingleTrack")}
                                                onClick={submitForm}
                                                disabled={isSubmitting || Object.keys(errors).length > 0}
                                            />
                                        </span>
                                    </Tooltip>
                                </div>
                            </>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default UnifiedEditSingleTrackModal;
